import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Briefcase, Zap, Star, Filter, TrendingUp, ChevronDown, Award, BookOpen, Link, Code, User, Database, Cloud, Smartphone, Terminal } from 'lucide-react';

// --- Type Definitions ---
interface Story {
  id: number;
  name: string;
  course: string;
  company: string;
  salaryLPA: number;
  testimonial: string;
  image: string; 
  category: 'Web Development' | 'App Development' | 'Programming' | 'Database';
}

interface BlogArticle {
  id: number;
  title: string;
  summary: string;
  link: string;
  icon: React.ElementType;
}

// --- Constants ---
const bannerImage = "url('https://images.unsplash.com/photo-1523240795612-9a054b0db644?q=80&w=2940&auto=format&fit=crop')"; 

// --- Mock Data ---
const mockStories: Story[] = [
  { id: 1, name: 'Rohan Sharma', course: 'Java Full Stack', company: 'TCS', salaryLPA: 4.5, testimonial: "PIT's training on Core Java and Spring Boot was spot on. I got placed immediately after completing the modules.", image: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=150&q=80', category: 'Web Development' },
  { id: 2, name: 'Sneha Patel', course: 'Mern Full Stack', company: 'Infosys', salaryLPA: 3.8, testimonial: "Learning React and Node.js here changed my career path. The projects were very practical and helped in the interview.", image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=150&q=80', category: 'Web Development' },
  { id: 3, name: 'Vikram Singh', course: 'Android Development', company: 'Capgemini', salaryLPA: 5.0, testimonial: "I always wanted to build apps. The Android course covered everything from UI to API integration perfectly.", image: 'https://images.unsplash.com/photo-1566492031773-4f4e44671857?auto=format&fit=crop&w=150&q=80', category: 'App Development' },
  { id: 4, name: 'Anjali Gupta', course: 'Python Programming', company: 'HCL Tech', salaryLPA: 3.5, testimonial: "Started from zero in coding. The Python mentor was very patient and explained concepts very clearly.", image: 'https://images.unsplash.com/photo-1567532939604-b6b5b0db2604?auto=format&fit=crop&w=150&q=80', category: 'Programming' },
  { id: 5, name: 'Rahul Verma', course: 'SQL Database', company: 'Accenture', salaryLPA: 4.2, testimonial: "Database management is crucial. Mastering SQL commands helped me crack the technical round easily.", image: 'https://images.unsplash.com/photo-1542909168-82c3e7fdca5c?auto=format&fit=crop&w=150&q=80', category: 'Database' },
  { id: 6, name: 'Pooja Reddy', course: 'Flutter', company: 'Tech Mahindra', salaryLPA: 4.8, testimonial: "Flutter is the future of hybrid apps. I'm glad I chose this course. The placement support was excellent.", image: 'https://images.unsplash.com/photo-1595152772835-219674b2a8a6?auto=format&fit=crop&w=150&q=80', category: 'App Development' },
  { id: 7, name: 'Arjun Das', course: 'Python Full Stack', company: 'Wipro', salaryLPA: 3.2, testimonial: "Django and Python combination is powerful. I got a good start as a fresher thanks to the guidance.", image: 'https://images.unsplash.com/photo-1600486913747-55e5470d6f40?auto=format&fit=crop&w=150&q=80', category: 'Web Development' },
  { id: 8, name: 'Kavita Mishra', course: 'ReactJS Development', company: 'Mindtree', salaryLPA: 4.0, testimonial: "Frontend development is fun! Building responsive UIs with React gave me the confidence to apply for jobs.", image: 'https://images.unsplash.com/photo-1589156280159-27698a70f29e?auto=format&fit=crop&w=150&q=80', category: 'Web Development' },
];

const mockBlogs: BlogArticle[] = [
    { id: 101, title: "Why Java Full Stack is Still King in 2026", summary: "Explore why enterprise companies still prefer Java for their backend systems.", link: "#", icon: Code },
    { id: 102, title: "Flutter vs Native Android: What to Choose?", summary: "A comparison for beginners looking to start a career in mobile app development.", link: "#", icon: Smartphone },
    { id: 103, title: "Cracking the SQL Interview", summary: "Top 20 SQL queries and concepts asked in fresher interviews.", link: "#", icon: Database },
    { id: 104, title: "Getting Started with Python", summary: "Why Python is the best language for beginners in programming.", link: "#", icon: Terminal },
];

// --- SEO SCHEMA ---
const storiesSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "PIT Student Success Stories & Placements",
  "description": "List of successful placements of PIT Institute students in top IT companies.",
  "itemListElement": mockStories.map((story, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "item": {
      "@type": "Person",
      "name": story.name,
      "jobTitle": `${story.course} Developer`,
      "worksFor": { "@type": "Organization", "name": story.company }
    }
  }))
};

const SuccessStoriesPage: React.FC = () => {
  const [filterCategory, setFilterCategory] = useState<string>('All');
  const [sortBy, setSortBy] = useState<'id' | 'salary' | 'company'>('id'); 

  const filteredStories = mockStories.filter(story => filterCategory === 'All' || story.category === filterCategory);
  const sortedStories = filteredStories.sort((a, b) => {
    if (sortBy === 'salary') return b.salaryLPA - a.salaryLPA; 
    if (sortBy === 'company') return a.company.localeCompare(b.company); 
    return a.id - b.id;
  });

  const categories = ['All', 'Web Development', 'App Development', 'Programming', 'Database'];

  const StoryCard: React.FC<{ story: Story }> = ({ story }) => (
    <div className="bg-white p-4 rounded-xl shadow-lg border-t-4 border-blue-600 transition-all duration-300 transform hover:shadow-2xl hover:translate-y-[-5px] motion-safe:animate-fadeIn relative group">
      <div className="absolute top-[-25px] left-1/2 transform -translate-x-1/2">
        <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200 border-4 border-white shadow-xl group-hover:border-orange-500 transition-all duration-300 flex items-center justify-center">
          <img src={story.image} alt={`${story.name} placed at ${story.company} as ${story.course} Developer`} className="w-full h-full object-cover" />
        </div>
      </div>
      <div className="text-center pt-8 mb-3">
        <h3 className="text-xl font-bold text-blue-900 line-clamp-1">{story.name}</h3>
        <p className="text-xs text-gray-500 flex items-center justify-center">
            <Star className="w-3 h-3 inline text-yellow-500 mr-1"/> {story.course}
        </p>
      </div>
      <div className="space-y-1 mb-3 border-b pb-3">
        <p className="text-sm font-semibold text-gray-700 flex items-center justify-between">
            <span className='flex items-center'><Briefcase className="w-4 h-4 mr-2 text-green-600"/> Placed At:</span> 
            <span className="ml-1 text-green-800 font-bold line-clamp-1">{story.company}</span>
        </p>
        <p className="text-lg font-extrabold text-orange-600 flex items-center justify-between">
            <span className='flex items-center'><Zap className="w-5 h-5 mr-2"/> Salary:</span>
            <span className="ml-1">{story.salaryLPA} LPA</span>
        </p>
      </div>
      <blockquote className="italic text-gray-700 text-xs border-l-4 border-orange-500 pl-3 py-1 bg-gray-50 rounded-md line-clamp-3">
        "{story.testimonial}"
      </blockquote>
    </div>
  );
  
  const BlogCard: React.FC<{ article: BlogArticle }> = ({ article }) => (
    <div className="bg-white rounded-xl shadow-lg p-6 border-b-4 border-orange-500 hover:shadow-xl transition duration-300 transform hover:-translate-y-1">
        <article.icon className="w-8 h-8 text-blue-600 mb-3" aria-hidden="true" />
        <h4 className="text-xl font-bold text-blue-900 mb-2">{article.title}</h4>
        <p className="text-base text-gray-600 mb-4 line-clamp-3">{article.summary}</p>
        <a href={article.link} className="flex items-center text-orange-500 font-medium hover:text-orange-700">
            Read Article <Link className="w-4 h-4 ml-1" aria-hidden="true" />
        </a>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-20 pt-16">
      <Helmet>
        <title>Success Stories & Placements | PIT Institute</title>
        <meta name="description" content="Discover the success stories of PIT Institute students. See how our IT training in Java, MERN, Python, and App Dev helped them secure high-paying MNC jobs." />
        <meta name="keywords" content="IT Placements, PIT Student Reviews, Software Developer Jobs, IT Career Success, Java Placements, MERN Stack Jobs" />
        <script type="application/ld+json">{JSON.stringify(storiesSchema)}</script>
      </Helmet>

      {/* 1. Inspirational Banner */}
      <div className="relative bg-blue-900 py-24 shadow-xl bg-cover bg-center" style={{ backgroundImage: bannerImage }}>
        <div className="absolute inset-0 bg-black opacity-60"></div>
        <div className="relative max-w-7xl mx-auto px-4 text-center text-white">
          <Award className="w-16 h-16 mx-auto text-yellow-400 mb-5 animate-pulse" />
          <h1 className="text-5xl md:text-6xl font-extrabold mb-3 tracking-wide font-aligran text-white drop-shadow-lg">Our Students, Our Pride</h1>
          <p className="text-xl md:text-2xl font-light max-w-4xl mx-auto text-gray-200">Real people, real results. See how our graduates are kickstarting their IT careers.</p>
        </div>
      </div>
      
      {/* 2. Controls (Filter & Sort) */}
      <div className="sticky top-16 z-20 bg-white shadow-md py-4 mb-8">
        <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0">
            <div className="flex flex-wrap gap-2 items-center justify-center sm:justify-start">
                <Filter className="w-5 h-5 text-blue-600 flex-shrink-0 mr-1" />
                <span className="font-semibold text-gray-700 mr-2 hidden md:inline">Filter By Domain:</span>
                {categories.map(cat => (
                    <button key={cat} onClick={() => setFilterCategory(cat)} className={`px-3 py-1 text-sm rounded-full font-medium transition duration-200 border ${filterCategory === cat ? 'bg-orange-500 text-white border-orange-500 shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                        {cat}
                    </button>
                ))}
            </div>
            <div className="flex items-center space-x-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                <span className="font-semibold text-gray-700 hidden sm:inline">Sort:</span>
                <div className="relative">
                    <select aria-label="Sort placements" value={sortBy} onChange={(e) => setSortBy(e.target.value as 'id' | 'salary' | 'company')} className="appearance-none bg-white border border-gray-300 text-gray-700 py-2 pl-3 pr-8 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm">
                         <option value="id">Latest</option>
                        <option value="salary">Highest Salary</option>
                        <option value="company">Company Name</option>
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
            </div>
        </div>
      </div>
      
      {/* 3. Success Stories Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h2 className="text-3xl font-bold text-blue-900 mb-8 border-b-4 border-orange-500 inline-block pb-2">Placements & Achievements</h2>
        {sortedStories.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-xl border border-gray-200">
                <p className="text-xl text-gray-500">No stories found for this category yet.</p>
                <button onClick={() => setFilterCategory('All')} className="mt-4 text-blue-600 hover:underline">View All Stories</button>
            </div>
        ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10"> 
                {sortedStories.map(story => (<StoryCard key={story.id} story={story} />))}
            </div>
        )}
      </div>
      
      <hr className="my-10 border-gray-300 max-w-7xl mx-auto"/>
      
      {/* 4. Blog Section */}
      <div className="max-w-7xl mx-auto px-4 pt-4 mb-16">
        <h2 className="text-3xl font-bold text-blue-900 mb-8 flex items-center">
            <BookOpen className="w-8 h-8 mr-3 text-orange-500"/> Career Insights
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {mockBlogs.map(blog => (<BlogCard key={blog.id} article={blog} />))}
        </div>
      </div>
    </div>
  );
};

export default SuccessStoriesPage;